import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, BarChart3, Coins, Store, Truck } from 'lucide-react'
import { get } from '@/lib/api'
import { money, num, percent } from '@/lib/format'
import { cn } from '@/lib/utils'
import { LoadError } from '@/components/ui/data-notice'
import { KpiTile } from '@/components/ui/kpi-tile'
import { PageHeader } from '@/components/ui/page-header'
import { Section } from '@/components/ui/section'
import { IndexBadge } from '@/components/ui/data-table'
import { Segmented, PERIOD_OPTIONS } from '@/components/ui/segmented'
import { DailyBars } from '@/components/charts/DailyBars'
import { Donut } from '@/components/charts/Donut'
import { RankBars } from '@/components/charts/RankBars'
import { Sparkline } from '@/components/charts/Sparkline'
import { SplitMeter } from '@/components/charts/SplitMeter'
import { compactMoney, countFormat, fillDaily } from '@/components/charts/chart-utils'
import { statusSegments } from '@/components/charts/statusSegments'
import { ShopReportView } from './ShopReportView'
import { AgencyMonthlyReport } from './MonthlyReport'

interface AgencyNetworkReport {
  days: number
  summary: {
    networkGMV: number
    totalOrders: number
    deliveredOrders: number
    deliveringOrders: number
    returnedOrders: number
    pendingOrders?: number
    cancelledOrders?: number
    otherOrders?: number
    deliveryRate: number
    returnRate: number
    /** Thu nhập mạng lưới của người xem theo dòng tiền: chênh lệch + hoa hồng − hoa hồng trả CTV. */
    totalAgencyNetCommission: number
    /** Đại lý xem: hoa hồng đã trả CTV; CTV xem: hoa hồng nhận được. */
    totalCtvCommission?: number
    /** Đơn cũ chưa có sổ tiền — không tính được thu nhập. */
    ordersWithoutLedger?: number
    activeShopsCount: number
    totalShopsCount: number
    totalCtvsCount: number
  }
  daily: Array<{
    date: string
    gmv: number
    ordersCount: number
    deliveredCount: number
    agencyCommission: number
  }>
  ctvLeaderboard: Array<{
    ctvId: string
    name: string
    phone: string
    shopsCount: number
    ordersCount: number
    gmv: number
    agencyCommission: number
  }>
  shopLeaderboard: Array<{
    shopId: string
    shopName: string
    ctvName: string
    totalOrders: number
    deliveredOrders: number
    returnedOrders: number
    gmv: number
    deliveryRate: number
    returnRate: number
    agencyCommission: number
  }>
  riskShops: Array<{
    shopId: string
    shopName: string
    ctvName: string
    totalOrders: number
    returnedOrders: number
    returnRate: number
    gmv: number
  }>
}

export function AgencyReportView() {
  const [activeTab, setActiveTab] = useState<'network' | 'monthly' | 'direct'>('network')
  const [days, setDays] = useState<number>(30)
  const [metric, setMetric] = useState<'orders' | 'gmv'>('orders')

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['reports.agency-network', days],
    queryFn: () => get<AgencyNetworkReport>(`/reports/agency-network?days=${days}`),
    enabled: activeTab === 'network',
  })

  const s = data?.summary
  const loading = isLoading && !data
  // Doanh số chỉ tính đơn không hoàn, không hủy — số đơn đi kèm cũng vậy.
  // totalOrders đã không gồm đơn hủy (máy chủ đếm riêng ở cancelledOrders)
  const salesOrders = Math.max(0, (s?.totalOrders ?? 0) - (s?.returnedOrders ?? 0))
  const dailyOrders = fillDaily(data?.daily, days, (d) => d.ordersCount)
  const dailyGmv = fillDaily(data?.daily, days, (d) => d.gmv)
  const dailyComm = fillDaily(data?.daily, days, (d) => d.agencyCommission)
  const topShops = (data?.shopLeaderboard ?? []).filter((x) => x.gmv > 0 || x.totalOrders > 0).slice(0, 8)
  const topCtvs = (data?.ctvLeaderboard ?? []).filter((x) => x.gmv > 0 || x.ordersCount > 0).slice(0, 8)

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <PageHeader
        title="Báo cáo"
        actions={
          <Segmented
            label="Chọn phạm vi báo cáo"
            value={activeTab}
            onChange={setActiveTab}
            options={[
              ['network', 'Mạng lưới'],
              ['monthly', 'Theo tháng'],
              // Đơn tự bán: chế độ Bán hàng → Báo cáo
            ]}
          />
        }
      />

      {activeTab === 'direct' ? (
        <ShopReportView isSubView />
      ) : activeTab === 'monthly' ? (
        <AgencyMonthlyReport />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2>Toàn mạng lưới</h2>
            <Segmented label="Chọn kỳ báo cáo" value={days} onChange={setDays} options={PERIOD_OPTIONS} />
          </div>

          {isError ? (
            <Section>
              <LoadError onRetry={() => refetch()}>Không tải được báo cáo mạng lưới.</LoadError>
            </Section>
          ) : (
            <>

              <div className="grid grid-cols-2 gap-3 min-[1200px]:grid-cols-4">
                <KpiTile
                  tone="blue"
                  icon={BarChart3}
                  label="COD dự kiến"
                  value={money(s?.networkGMV ?? 0)}
                  hint={`${countFormat.format(salesOrders)} đơn`}
                  loading={loading}
                >
                  <Sparkline values={dailyGmv.map((d) => d.value)} label={`COD dự kiến từng ngày trong ${days} ngày`} />
                </KpiTile>
                <KpiTile
                  tone="violet"
                  icon={Coins}
                  label="Chiết khấu Đại lý"
                  value={money(s?.totalAgencyNetCommission ?? 0)}
                  hint={[
                    s?.totalCtvCommission ? `Đã trừ ${money(s.totalCtvCommission)} chiết khấu CTV` : null,
                    s?.ordersWithoutLedger ? `${countFormat.format(s.ordersWithoutLedger)} đơn chưa tính` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  loading={loading}
                >
                  <Sparkline values={dailyComm.map((d) => d.value)} label={`Chiết khấu từng ngày trong ${days} ngày`} />
                </KpiTile>
                <KpiTile
                  tone="green"
                  icon={Truck}
                  label="Tỷ lệ giao thành công"
                  value={percent(s?.deliveryRate ?? 0)}
                  hint={`${countFormat.format(s?.deliveredOrders ?? 0)}/${countFormat.format(s?.totalOrders ?? 0)} đơn`}
                  loading={loading}
                >
                  <SplitMeter
                    parts={[
                      { label: 'Đã giao', value: s?.deliveredOrders ?? 0, barClass: 'bg-toneFg' },
                      {
                        label: 'Còn lại',
                        value: Math.max(0, (s?.totalOrders ?? 0) - (s?.deliveredOrders ?? 0)),
                        barClass: 'bg-toneFg/25',
                      },
                    ]}
                    format={(n) => countFormat.format(n)}
                    textClass="text-toneFg"
                  />
                </KpiTile>
                <KpiTile
                  tone="orange"
                  icon={Store}
                  label="Shop có đơn"
                  value={`${countFormat.format(s?.activeShopsCount ?? 0)}/${countFormat.format(s?.totalShopsCount ?? 0)}`}
                  hint={`${countFormat.format(s?.totalCtvsCount ?? 0)} CTV`}
                  loading={loading}
                >
                  <SplitMeter
                    parts={[
                      { label: 'Có đơn', value: s?.activeShopsCount ?? 0, barClass: 'bg-toneFg' },
                      {
                        label: 'Chưa có đơn',
                        value: Math.max(0, (s?.totalShopsCount ?? 0) - (s?.activeShopsCount ?? 0)),
                        barClass: 'bg-toneFg/25',
                      },
                    ]}
                    format={(n) => countFormat.format(n)}
                    textClass="text-toneFg"
                  />
                </KpiTile>
              </div>

              {data?.riskShops && data.riskShops.length > 0 && (
                <Section
                  title={
                    <span className="inline-flex items-center gap-2 text-danger">
                      <AlertTriangle className="size-4" aria-hidden />
                      {data.riskShops.length} shop hoàn nhiều
                    </span>
                  }
                >
                  <ul className="divide-y divide-rule">
                    {data.riskShops.map((rs, i) => (
                      <li key={rs.shopId} className="flex items-center justify-between gap-3 px-4 py-3">
                        <div className="min-w-0">
                          <p className="flex items-center gap-2 text-sm font-medium text-ink">
                            <IndexBadge n={i + 1} />
                            <span className="truncate">{rs.shopName}</span>
                          </p>
                          <p className="text-xs text-muted">
                            {rs.ctvName} · {rs.returnedOrders}/{rs.totalOrders} đơn hoàn
                          </p>
                        </div>
                        <span className="shrink-0 rounded-full bg-danger/10 px-2 py-0.5 text-xs font-semibold tabular-nums text-danger">
                          {percent(rs.returnRate, 0)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)]">
                <Section
                  title={`${days} ngày qua`}
                  action={
                    <Segmented
                      label="Chọn số liệu"
                      value={metric}
                      onChange={setMetric}
                      options={[
                        ['orders', 'Số đơn'],
                        ['gmv', 'COD dự kiến'],
                      ]}
                    />
                  }
                >
                  <div className={cn('p-4 pt-5 transition-opacity', loading && 'opacity-50')}>
                    {metric === 'orders' ? (
                      <DailyBars
                        data={dailyOrders}
                        format={(n) => `${countFormat.format(n)} đơn`}
                        tickFormat={(n) => countFormat.format(n)}
                        label="Số đơn mỗi ngày"
                        integer
                      />
                    ) : (
                      <DailyBars data={dailyGmv} format={money} tickFormat={compactMoney} label="COD dự kiến mỗi ngày" />
                    )}
                    <p className="mt-3 text-xs text-muted">
                      Tổng {days} ngày: {countFormat.format(s?.totalOrders ?? 0)} đơn · {money(s?.networkGMV ?? 0)} COD dự kiến.
                    </p>
                  </div>
                </Section>
                <Section title="Trạng thái đơn" action={<span className="text-xs text-muted">{days} ngày qua</span>}>
                  <div className="p-4">
                    <Donut emptyLabel="Chưa có đơn" segments={statusSegments(s)} />
                  </div>
                </Section>
              </div>

              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                <Section title="Shop bán tốt nhất">
                  {topShops.length ? (
                    <RankBars
                      rows={topShops.map((x) => ({
                        key: x.shopId,
                        label: x.shopName,
                        meta: (
                          <>
                            <span>{countFormat.format(x.totalOrders)} đơn</span>
                            <span>· giao {percent(x.deliveryRate, 0)}</span>
                            {x.ctvName && <span className="truncate">· {x.ctvName}</span>}
                          </>
                        ),
                        value: x.gmv,
                        display: compactMoney(x.gmv),
                      }))}
                    />
                  ) : (
                    <p className="px-4 py-10 text-center text-sm text-muted">Chưa có đơn</p>
                  )}
                </Section>
                <Section title="CTV bán tốt nhất">
                  {topCtvs.length ? (
                    <RankBars
                      rows={topCtvs.map((c) => ({
                        key: c.ctvId,
                        label: c.name,
                        meta: (
                          <>
                            <span>{countFormat.format(c.ordersCount)} đơn</span>
                            <span>· {countFormat.format(c.shopsCount)} shop</span>
                            {c.agencyCommission > 0 && <span>· chiết khấu {compactMoney(c.agencyCommission)}</span>}
                          </>
                        ),
                        value: c.gmv,
                        display: compactMoney(c.gmv),
                      }))}
                    />
                  ) : (
                    <p className="px-4 py-10 text-center text-sm text-muted">Chưa có đơn</p>
                  )}
                </Section>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
